// "Your access to this was removed" — the ONE place that decides a refusal means
// a share is gone, and the one message the user reads when it does.
//
// WHY THIS IS A SHARED HELPER RATHER THAN THREE `if (status === 404)` CHECKS.
// Three routes can now be called against a shared row (cadence, watchlist, copy),
// and all three answer 404 for a row the viewer may no longer read — the same
// 404 they answer for a row that never existed, deliberately, because a
// distinguishable "revoked" would disclose that the forecast is still there. So
// the code that INTERPRETS that status is the code that decides what the user is
// told, and having one of those beats having three that drift.
//
// Revocation is not pushed: the owner unticking a box does not reach into anyone
// else's open tab (a deliberate product decision — a row vanishing mid-read is
// worse than a row that is briefly stale). A recipient's list can therefore hold
// a row the server would now refuse, and the refusal is the discovery.
//
// 404 ONLY. A 403 is NOT access loss — `out_of_org` means "you can still see
// this, you just cannot change it", and dropping the row for that would delete a
// forecast the user still holds. 401 is not access loss either: the session
// expired, and every row would go, not this one.

import { sharedAccessLost } from "./reducer.js";

/** What the user is told. Plain, and it says what to do next. */
export const SHARED_ACCESS_LOST_MESSAGE =
  "This forecast is no longer shared with you. It has been removed from your list.";

/**
 * True iff `status` means "this share is gone" rather than any other refusal.
 *
 * @param {number|undefined|null} status
 * @returns {boolean}
 */
export const isAccessLost = (status) => status === 404;

/**
 * Handle a refused action on a SHARED row: drop the row and return the message
 * to show, or null when the refusal was something else entirely.
 *
 * Returns rather than renders so the caller owns presentation (a toast, a banner,
 * a panel close) while the decision stays here.
 *
 * @param {{ dispatch: Function, runId: string, status: number|undefined|null, isShared: boolean }} args
 *   `dispatch` is the run-store's (`runStore.dispatch`), injected so this stays a pure decision.
 * @returns {string|null} the message to surface, or null for "not access loss"
 */
export function noteSharedAccessLoss({ dispatch, runId, status, isShared }) {
  // OWNER ROWS ARE EXEMPT, and not as an optimization. An owner's 404 means
  // something else went wrong — a deleted row, a bad id, a bug — and dropping
  // their own forecast out of their own list on the strength of one failed
  // request would be destroying the user's view of their own work.
  if (!isShared || !isAccessLost(status)) return null;
  dispatch(sharedAccessLost(runId));
  return SHARED_ACCESS_LOST_MESSAGE;
}
